/**
 * The call's stage on a phone, every case of `docs/specs/room-layout.md` (decision 162): what fills the
 * stage and where the camera tiles sit, in portrait and in landscape, by the match (none, landscape,
 * portrait), side-by-side, a share, and the cameras shown or hidden. Desktop keeps its own layout (the
 * stage, the tiles on the right edge, the share's 320 column), which already reads as that file's D rows.
 * Pure, so every cell is tested.
 *
 * - The main picture (the match, or a share) is centered at the largest size that fits, at its aspect.
 * - In portrait the tiles are a strip directly under it, touching it, the block of the two centered in the
 *   height; with a share, the strip carries the match first (one panel: a phone's small tile hides and
 *   pauses a side-by-side's second, job 2), then the cameras.
 * - In landscape a single landscape picture is near full screen, the tiles over its top right corner; a
 *   portrait one or a stacked side-by-side leaves a column at the right, unless none fits. With a share, the column carries
 *   the match first (one panel, as in portrait), then the cameras.
 * - Hidden cameras take the tiles away and the main picture takes the space.
 * - Side-by-side is two panels of equal size stacked, on a phone in either orientation.
 */
export interface Rect { x: number; y: number; w: number; h: number }

export const STRIP_H = 96;
/** A strip or column that carries the match, tall or wide enough for the match's own bar. */
export const STRIP_WITH_MATCH_H = 112;
export const COLUMN_W = 120;
export const COLUMN_WITH_MATCH_W = 168;
export const GAP = 8;
/** A corner's tiles: this wide each, stacked at the top right. */
export const CORNER_W = 96;

export interface PhoneStageInput {
  portrait: boolean;
  /** The stage's box. */
  area: { w: number; h: number };
  /** The match open: its video's aspect (width / height), and whether a side-by-side is open. */
  match: { aspect: number; sideBySide: boolean } | null;
  /** A share on the stage: its aspect. */
  share: { aspect: number } | null;
  camerasShown: boolean;
  /** The camera feeds' aspects, theirs then this side's. */
  tiles: [number, number];
}

export interface PhoneStage {
  /** What fills the stage: the match block (both panels when side by side) or the share. */
  main: Rect;
  /** The match player's box: the main box, or its place in the strip or column while a share has the stage. */
  match: Rect | null;
  /** Where the tiles sit, and in which form; null when there are none. */
  tiles: { form: 'strip' | 'column' | 'corner'; box: Rect; items: Rect[] } | null;
}

/** A box of this aspect, as large as fits inside w by h. */
function fit(aspect: number, w: number, h: number): { w: number; h: number } {
  if (w <= 0 || h <= 0 || !(aspect > 0)) return { w: Math.max(0, w), h: Math.max(0, h) };
  return w / h > aspect ? { w: Math.round(h * aspect), h: Math.round(h) } : { w: Math.round(w), h: Math.round(w / aspect) };
}

/** The match block's aspect: one panel, or two of them stacked. */
function matchBlockAspect(m: { aspect: number; sideBySide: boolean }): number {
  return m.sideBySide ? m.aspect / 2 : m.aspect;
}

/** Items laid along a row of this height (each at its aspect), or down a column of this width. */
function lay(aspects: number[], box: Rect, row: boolean): Rect[] {
  const out: Rect[] = [];
  let at = row ? box.x : box.y;
  for (const a of aspects) {
    const size = row ? { w: Math.round(box.h * a), h: box.h } : { w: box.w, h: Math.round(box.w / a) };
    out.push(row ? { x: at, y: box.y, ...size } : { x: box.x, y: at, ...size });
    at += (row ? size.w : size.h) + GAP;
  }
  return out;
}

export function phoneStage(p: PhoneStageInput): PhoneStage {
  const { w: W, h: H } = p.area;
  const cams = p.camerasShown ? p.tiles : [];
  // What goes beside the main picture: with a share, the match's place first (its panels), then the cameras.
  // One panel there even when side by side: a phone's small tile hides and pauses the second (job 2).
  const matchBeside = p.share && p.match ? [p.match.aspect] : [];
  const besideAspects = [...matchBeside, ...cams];
  const mainAspect = p.share ? p.share.aspect : p.match ? matchBlockAspect(p.match) : 16 / 9;
  const carriesMatch = matchBeside.length > 0;

  if (besideAspects.length === 0) {
    const m = fit(mainAspect, W, H);
    const main = { x: Math.round((W - m.w) / 2), y: Math.round((H - m.h) / 2), ...m };
    return { main, match: p.match && !p.share ? main : null, tiles: null };
  }

  if (p.portrait) {
    // A strip under the main picture, touching it; the two centered in the height together.
    // As tall as the strip wants, but never wider than the screen: the tiles keep their aspects.
    const sumW = besideAspects.reduce((a, b) => a + b, 0);
    const sh = Math.floor(Math.min(carriesMatch ? STRIP_WITH_MATCH_H : STRIP_H, (W - GAP * (besideAspects.length - 1)) / sumW));
    const m = fit(mainAspect, W, H - sh - GAP);
    const top = Math.round((H - (m.h + GAP + sh)) / 2);
    const main = { x: Math.round((W - m.w) / 2), y: top, ...m };
    const stripY = top + m.h + GAP;
    const items = lay(besideAspects, { x: 0, y: stripY, w: W, h: sh }, true);
    // Centered under the main picture; narrower than the screen when it can be.
    const used = items.length ? items[items.length - 1].x + items[items.length - 1].w : 0;
    const shift = Math.max(0, Math.round((W - used) / 2));
    const shifted = items.map((r) => ({ ...r, x: r.x + shift }));
    const box = { x: shift, y: stripY, w: Math.min(W, used), h: sh };
    return {
      main,
      match: p.match ? (p.share ? shifted[0] : main) : null,
      tiles: { form: 'strip', box, items: shifted },
    };
  }

  // Landscape: the main picture fills the height; the tiles a column at the right in the width it leaves,
  // or over the top right corner when no column fits.
  // As wide as the column wants, but never taller than the screen: the tiles keep their aspects.
  const sumH = besideAspects.reduce((a, b) => a + 1 / b, 0);
  const cw = Math.floor(Math.min(carriesMatch ? COLUMN_WITH_MATCH_W : COLUMN_W, (H - GAP * (besideAspects.length - 1)) / sumH));
  const full = fit(mainAspect, W, H);
  // A single landscape picture is near full screen with the tiles over its corner (L3); a portrait one or a
  // stacked side-by-side leaves a width at the right for the column (L11, L7), unless it doesn't fit.
  const nearFull = !carriesMatch && mainAspect >= 1;
  if (!carriesMatch && (nearFull || W - full.w < cw + GAP)) {
    const main = { x: Math.round((W - full.w) / 2), y: Math.round((H - full.h) / 2), ...full };
    const items = lay(cams, { x: W - CORNER_W - GAP, y: GAP, w: CORNER_W, h: H }, false);
    return { main, match: p.match ? main : null, tiles: { form: 'corner', box: unionOf(items), items } };
  }
  const m = fit(mainAspect, W - cw - GAP, H);
  const main = { x: Math.round((W - cw - GAP - m.w) / 2), y: Math.round((H - m.h) / 2), ...m };
  const items = lay(besideAspects, { x: W - cw, y: 0, w: cw, h: H }, false);
  const usedH = items.length ? items[items.length - 1].y + items[items.length - 1].h : 0;
  const down = Math.max(0, Math.round((H - usedH) / 2));
  const placed = items.map((r) => ({ ...r, y: r.y + down }));
  return {
    main,
    match: p.match ? (p.share ? placed[0] : main) : null,
    tiles: { form: 'column', box: { x: W - cw, y: down, w: cw, h: Math.min(H, usedH) }, items: placed },
  };
}

/** The box around several. */
function unionOf(rs: Rect[]): Rect {
  const x = Math.min(...rs.map((r) => r.x));
  const y = Math.min(...rs.map((r) => r.y));
  const r = Math.max(...rs.map((q) => q.x + q.w));
  const b = Math.max(...rs.map((q) => q.y + q.h));
  return { x, y, w: r - x, h: b - y };
}
